import '../../../assets/styles/tools/imageRemoveBg.css';
import '../../../assets/styles/App.css';
import React, {MouseEvent, useCallback, useEffect, useRef, useState, WheelEvent} from "react";
import {useImageEditorInfoStore} from "../../../store/imageEditorInfoStore";
import {CubeGridLoader} from "../../loading/CubeGridLoader";
import {ColorPicker, Segmented, Slider, Tooltip} from "antd";
import {Grab, Layers2, Plus, Redo, RotateCcw, RotateCw, Trash2, Undo, Upload, X, ZoomIn, ZoomOut} from 'lucide-react';
import type {AggregationColor} from "antd/es/color-picker/color";
import html2canvas from "html2canvas";
import {basicDownloadImage, drawLines} from "../../../utils";
import {DemoBgImageList, ModelTypeConstant} from "../../../constants";
import {doImageInPaint} from "./imgInpaint";
import {useImageInPaintEditorInfoStore} from "../../../store/imageInPaintEditorInfoStore";
import {throttle_} from "../../../utils/debounceThrottle";
import {Line} from "../../../type";
import {useAlertMessage} from "../../../hooks/AlertContext";
import {modelIsLoaded} from "./imgInpaint";

type IImageEditorProps = {
    closeEditorTable?: () => void;
}

const maxWidthSize = 900;// 图片宽最大为900px
const maxHeightSize = 600;// 图片高最大为600px
const minWidthSize  = 300;// 图片宽最小为300px
const minHeightSize = 300; // 图片高最小为 300px

const minScale= 1;// 图片最小放缩倍率
const maxScale = 10;// 图片最大放缩倍率
const zoomIntensity = 0.1;// 图片缩放速度

const defaultPenSize = 160;// 默认画笔的大小，需要乘上倍率得到最终的大小
const sliderMin = 10;// 滑块最小倍率
const sliderMax = 100;// 滑块最大倍率

let curPaintOrEraseLinesIndex = -1;// 当前上次处理的线条在上面线条数组Lines中的下标位置
export const ImageInPaintEditor: React.FC<IImageEditorProps> = ({
    closeEditorTable = () => {}
}) => {
    const {showAlert} = useAlertMessage();

    const {imageObj, setImageObj} = useImageInPaintEditorInfoStore();
    const [original, setOriginalLoaded] = useState(imageObj.src);

    const canvasContainerRef = useRef<HTMLDivElement>(null);
    const originalCanvasRef = useRef<HTMLImageElement>(null);
    const [maskCanvas] = useState<HTMLCanvasElement>(() => {// 最终涂鸦得到的蒙层画布
        return document.createElement('canvas')
    })

    const [processResultImages, setProcessResultImages] = useState<string[]>([]);// 存放所有处理过的结果图片，也包括最初的图片

    const canvasDrawContainerRef = useRef<HTMLCanvasElement>(null);
    const [context, setContext] = useState<CanvasRenderingContext2D>();// 绘图上下文

    const [renders, setRenders] = useState<HTMLImageElement[]>([])
    const [lines, setLines] = useState<Line[]>([{ pts: [], src: '' }]);// 绘制的曲线条数组

    const [isLoading, setIsLoading] = useState(true);
    const [isConverted, setConverted] = useState(false);// 图像是否已经转换完成，即处理完成

    const [scale, setScale] = useState(1);// 当前图片的缩放率
    const [transformOrigin, setTransformOrigin] = useState('50% 50%');// 缩放基点
    const [translateX, setTranslateX] = useState(0);// 图片X轴平移
    const [translateY, setTranslateY] = useState(0);// 图片Y轴平移
    const dragState = useRef({ isDragging: false, startX: 0, startY: 0 }); // 拖动状态

    const [isShowPenOrEraser, setIsShowPenOrEraser] = useState(true);// 是否显示画笔或者橡皮擦
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });// 鼠标位置
    const [penSize, setPenSize] = useState({
        width: defaultPenSize * 30 / 100,
        height: defaultPenSize * 30 / 100,
    });// 画笔的尺寸
    const [penColor, setPenColor] = useState('#FF8D00');// 画笔的颜色

    const [grabColor, setGrabColor] = useState('black');// grab图标的颜色（开启拖拽图片功能就显示其他颜色，关闭则是默认黑色）
    const [isGrabImg, setIsGrabImg] = useState(false);// 是否开启拖拽图片功能

    const fileInputRef = useRef<HTMLInputElement>(null);// 文件上传ref

    const [showImageStyle, setShowImageStyle] = useState({
        backgroundColor: '#fff',
        backgroundImage:
            'linear-gradient(45deg, rgb(240, 240, 240) 25%, transparent 0px),' +
            'linear-gradient(45deg, transparent 75%, rgb(240, 240, 240) 0px),' +
            'linear-gradient(45deg, rgb(240, 240, 240) 25%, transparent 0px),' +
            'linear-gradient(45deg, transparent 75%, rgb(240, 240, 240) 0px)',
        backgroundPosition: '0px 0px, 10px 10px, 10px 10px, 20px 20px',
        backgroundClip: 'content-box',
        backgroundSize: '20px 20px',
    });

    const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });

    useEffect(() => {
        setIsLoading(false);

        curPaintOrEraseLinesIndex = -1;// 初始化当前上次处理的线条在上面线条数组Lines中的下标位置
        // 监听窗口大小变化
        const handleResize = () => {
            setWindowSize({ width: window.innerWidth, height: window.innerHeight });
        };
        window.addEventListener("resize", handleResize);
        return () => {
            processResultImages.map((blobUrl) => URL.revokeObjectURL(blobUrl));// 内存清理
            window.removeEventListener("resize", handleResize);
        }
    }, []);

    useEffect(() => {
        if (canvasDrawContainerRef.current && !context) {
            const ctx = canvasDrawContainerRef.current.getContext('2d');
            if (ctx) setContext(ctx);
        }
    }, [canvasDrawContainerRef]);

    const getReasonableSize = (width: number, height: number) => {
        const aspectRatio = width / height; // 原始宽高比

        let newWidth = width;
        let newHeight = height;

        // 根据窗口大小调整图片宽度
        if (windowSize.width < maxWidthSize) {
            newWidth = windowSize.width * 0.5; // 图片宽度为窗口宽度的 50%
            newHeight = newWidth / aspectRatio; // 保持宽高比
        }

        // 如果调整后的尺寸超出最大限制，按最大限制调整
        if (newWidth > maxWidthSize || newHeight > maxHeightSize) {
            if (newWidth > maxWidthSize) {
                newWidth = maxWidthSize;
                newHeight = newWidth / aspectRatio;
            }
            if (newHeight > maxHeightSize) {
                newHeight = maxHeightSize;
                newWidth = newHeight * aspectRatio;
            }
        }

        // 如果调整后的尺寸小于最小限制，按最小限制调整
        if (newWidth < minWidthSize || newHeight < minHeightSize) {
            if (newWidth < minWidthSize) {
                newWidth = minWidthSize;
                newHeight = newWidth / aspectRatio;
            }
            if (newHeight < minHeightSize) {
                newHeight = minHeightSize;
                newWidth = newHeight * aspectRatio;
            }
        }

        return { newWidth, newHeight };
    }

    useEffect(() => {
        const container = canvasContainerRef.current;
        if (imageObj && container) {
            setOriginalLoaded(imageObj.src);
            const img = new Image();
            img.src = imageObj.src;
            img.onload = () => {
                const {newWidth, newHeight} = getReasonableSize(img.width, img.height);
                container.style.minWidth = `min(${newWidth}px, 100%)`;
                container.style.maxWidth = '100%';
                container.style.width = newWidth + 'px';
                container.style.minHeight = `min(80%, ${newHeight}px)`;
                container.style.maxHeight = '80%';
                container.style.height = newHeight + 'px';
            };
        }
    }, [imageObj, windowSize]);

    /*--------------------Draw Event start--------------------*/
    const draw =
        (index = -1) => {
            const original = originalCanvasRef.current;
            if (!context || !original) {
                return
            }
            context.clearRect(0, 0, context.canvas.width, context.canvas.height)
            const currRender =
                renders[index === -1 ? renders.length - 1 : index] ?? original
            const { canvas } = context

            const divWidth = canvasContainerRef.current!.clientWidth
            const divHeight = canvasContainerRef.current!.clientHeight

            // 计算宽高比
            const imgAspectRatio = currRender.width / currRender.height
            const divAspectRatio = divWidth / divHeight

            let canvasWidth
            let canvasHeight

            // 比较宽高比以决定如何缩放
            if (divAspectRatio > imgAspectRatio) {
                // div 较宽，基于高度缩放
                canvasHeight = divHeight
                canvasWidth = currRender.width * (divHeight / currRender.height)
            } else {
                // div 较窄，基于宽度缩放
                canvasWidth = divWidth
                canvasHeight = currRender.height * (divWidth / currRender.width)
            }

            canvas.width = canvasWidth
            canvas.height = canvasHeight

            if (currRender?.src) {
                context.drawImage(currRender, 0, 0, canvas.width, canvas.height)
            } else {
                context.drawImage(original, 0, 0, canvas.width, canvas.height)
            }
            drawLines(context, lines.slice(0, curPaintOrEraseLinesIndex + (dragState.current.isDragging ? 2 : 1)), "rgba(0, 255, 0, 0.5)");
        }


    const onPaint = (px: number, py: number) => {// 某条线未画完，正在绘制中
        const curLine = lines[curPaintOrEraseLinesIndex + 1];
        curLine.pts.push({ x: px, y: py });
        draw();
    };

    const clearAllPaint = () => {
        curPaintOrEraseLinesIndex = -1;
        setLines([{ pts: [], src: '' }]);
        if (context) context.clearRect(0, 0, context.canvas.width, context.canvas.height)
    }

    const onMouseDrag = (ev: MouseEvent) => {
        const canvas = canvasDrawContainerRef.current;
        if (!canvas) return;

        // 获取鼠标窗口坐标
        const mouseX = ev.clientX;
        const mouseY = ev.clientY;

        // 获取canvas的边界矩形
        const rect = canvas.getBoundingClientRect();

        // 计算相对于canvas的坐标
        const relX = mouseX - rect.left;
        const relY = mouseY - rect.top;

        // 转换为canvas绘制坐标
        const canvasX = (relX / rect.width) * canvas.width;
        const canvasY = (relY / rect.height) * canvas.height;

        onPaint(canvasX, canvasY);
    };
    /*----------------------------------Draw Event End----------------------------*/

    const setIsDragImg = () => {
        setGrabColor(isGrabImg ? 'black' : '#C56D00');
        setIsGrabImg(!isGrabImg);
        setIsShowPenOrEraser(false);
    }
    const uploadFile = (ev: any) => {// 重新上传文件
        if (isLoading) {// 正在加载中
            showAlert({
                message: "A new task is running!!",
                type: "warning"
            })
            return;
        }
        setIsLoading(false);//重置
        const file = ev.currentTarget.files?.[0];
        console.log(file);
        if (file) {
            const imageUrl = URL.createObjectURL(file);
            const image = new Image();
            image.src = imageUrl;
            image.onload = () => {
                setOriginalLoaded(imageUrl);
                setImageObj(image);
            };
        }
    }

    const handleUpload = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';// 重置文件输入值
            fileInputRef.current.click();
        }
    }

    const undoDraw = () => {// 回到上一步操作
        if (curPaintOrEraseLinesIndex <= -1 || lines.length === 1) {// 已经不能返回了
            showAlert({
                message: "Undo completed",
                type: "warning"
            })
            return ;
        }
        --curPaintOrEraseLinesIndex;
        draw();
    }

    const redoDraw = () => {// 重新执行下一步操作
        if (curPaintOrEraseLinesIndex >= lines.length - 2) {
            showAlert({
                message: "Redo completed",
                type: "warning"
            })
            return ;
        }
        ++curPaintOrEraseLinesIndex;
        draw();
    }
    const zoomInImage = () => {
        const newScale = scale + zoomIntensity;
        if (newScale < minScale || newScale > maxScale) return;
        setScale(newScale);
    }

    const zoomOutImage = () => {
        const newScale = scale - zoomIntensity;
        if (newScale < minScale || newScale > maxScale) return;
        setScale(newScale);

        // 重置平移当缩放到等于 1
        if (newScale === 1) {
            setTranslateX(0);
            setTranslateY(0);
            setTransformOrigin('50% 50%');
        }
    }

    const zoomImage = (e: WheelEvent) => {
        // 计算缩放比例
        const delta = e.deltaY;
        const newScale = delta > 0 ? scale - zoomIntensity : scale + zoomIntensity;

        if (newScale < minScale || newScale > maxScale) return;

        // 计算鼠标相对位置
        const rect = canvasContainerRef.current?.getBoundingClientRect();
        if (!rect) return;

        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        setTransformOrigin(`${mouseX}px ${mouseY}px`);
        setScale(newScale);

        if (newScale === 1) {
            // 重置平移当缩放到等于 1
            setTranslateX(0);
            setTranslateY(0);
            setTransformOrigin('50% 50%');// 重置缩放基点

            // 触发一次拖动事件
            const startX = mouseX, startY = mouseY;
            const endX = mouseX + 50, endY = mouseY + 50;
            if (canvasContainerRef.current) {
                // 触发拖拽开始
                const dragStartEvent = new DragEvent('dragstart', { bubbles: true });
                dragStartEvent.dataTransfer?.setData('text/plain', 'item-id');
                canvasContainerRef.current.dispatchEvent(dragStartEvent);

                // 触发拖拽移动
                const dragEvent = new DragEvent('drag', {
                    clientX: startX,
                    clientY: startY
                });
                canvasContainerRef.current.dispatchEvent(dragEvent);

                // 触发拖拽结束
                const dragEndEvent = new DragEvent('dragend', {
                    clientX: endX,
                    clientY: endY
                });
                canvasContainerRef.current.dispatchEvent(dragEndEvent);
            }
        }
    }

    // 画笔和橡皮擦移动，实际就是鼠标移动
    const handlePenOrEraserMove = (e: MouseEvent) => {
        setTimeout(() => {
            if(!isShowPenOrEraser) setIsShowPenOrEraser(true);
            if (canvasContainerRef.current) {
                const rect = canvasContainerRef.current.getBoundingClientRect();
                if (e.clientX >= rect.left && e.clientX <= rect.right &&
                    e.clientY >= rect.top && e.clientY <= rect.bottom) {
                    setMousePosition({
                        x: e.clientX - rect.left,
                        y: e.clientY - rect.top
                    });
                }
            }
        }, 0);
    };

    // 鼠标按下
    const handleMouseDown = (event: MouseEvent) => {
        if (isGrabImg && scale <= 1) return; // 拖动图片时，仅当放大后才允许拖动

        if (!isGrabImg) {// 画笔状态时设置画笔线条的尺寸
            if (curPaintOrEraseLinesIndex !== lines.length - 2) // 有Undo过，没有完全恢复
                setLines([...lines.slice(0 , curPaintOrEraseLinesIndex + 1), { pts: [], src: '' }]);

            lines[curPaintOrEraseLinesIndex + 1].lineWidth = penSize.width / 2;
            console.log(penSize.width)
        }

        event.preventDefault();
        dragState.current = {
            isDragging: true,
            startX: event.clientX - translateX,
            startY: event.clientY - translateY,
        };
    };

    // 鼠标移动
    const handleMouseMove = (event: MouseEvent) => {
        if (isGrabImg) {
            if (!dragState.current.isDragging) return;

            let newTranslateX = event.clientX - dragState.current.startX;
            let newTranslateY = event.clientY - dragState.current.startY;

            const img = originalCanvasRef.current;
            if (!img) return;

            const rect = img.getBoundingClientRect();
            const maxTranslateX = (rect.width * scale - rect.width) / 2;
            const maxTranslateY = (rect.height * scale - rect.height) / 2;

            newTranslateX = Math.min(Math.max(newTranslateX, -maxTranslateX), maxTranslateX);
            newTranslateY = Math.min(Math.max(newTranslateY, -maxTranslateY), maxTranslateY);
            setTranslateX(newTranslateX);
            setTranslateY(newTranslateY);
        }else {// 当前正在画图或者擦图
            handlePenOrEraserMove(event);
            if (!dragState.current.isDragging) return;
            onMouseDrag(event);
        }
    };

    // 鼠标松开
    const handleMouseUp = () => {
        dragState.current.isDragging = false;
        if (!isGrabImg) {// 绘制完松开鼠标时结束旧的线条，创建新的线条
            lines.push({ pts: [], src: '' });
            ++curPaintOrEraseLinesIndex;// 当前的线条在线条数组中的下标 + 1
        }
    };

    // 鼠标离开容器时停止拖动
    const handleMouseLeave = () => {
        dragState.current.isDragging = false;
        setIsShowPenOrEraser(false);
    };

    const sliderSizeChange = (v: any) => {
        if (canvasContainerRef.current) {
            setIsShowPenOrEraser(true);// 显示画笔或者橡皮擦
            const canvas = canvasContainerRef.current;
            const rect = canvas.getBoundingClientRect();
            const centerX = rect.left + Number(canvas?.style.width) / 2;
            const centerY = rect.top + Number(canvas?.style.height) / 2;
            setMousePosition({ x: centerX + v, y: centerY + v });
        }
        setPenSize({
            width: defaultPenSize * v / 100,
            height: defaultPenSize * v / 100
        });
    }

    const refreshCanvasMask = useCallback(() => {
        if (!context?.canvas.width || !context?.canvas.height) {
            throw new Error('canvas has invalid size')
        }
        maskCanvas.width = context?.canvas.width
        maskCanvas.height = context?.canvas.height
        const ctx = maskCanvas.getContext('2d')
        if (!ctx) {
            throw new Error('could not retrieve mask canvas')
        }
        drawLines(ctx, lines.slice(0, curPaintOrEraseLinesIndex + (dragState.current.isDragging ? 2 : 1)), 'white')
    }, [context?.canvas.height, context?.canvas.width, lines, maskCanvas])

    const inpaint = async () => {
        if (!modelIsLoaded()) {
            showAlert({
                message: "Loading model and trying again later",
                type: "warning",
            })
            return;
        }
        if (isLoading) {// 正在加载中
            showAlert({
                message: "A new task is running!!",
                type: "warning"
            })
            return;
        }
        if (lines.length <= 1) {// 还没有绘制
            showAlert({
                message: "Please select the area to inpaint before proceeding.",
                type: "warning"
            })
            return;
        }
        if (!canvasDrawContainerRef.current || !originalCanvasRef.current) return;

        setIsLoading(true);

        refreshCanvasMask();
        const maskBase64 = maskCanvas?.toDataURL("image/png");
        doImageInPaint(originalCanvasRef.current, maskBase64, showAlert).then(async (res: any) => {
            if (!res) return;

            if (processResultImages.length < 5)// 最多存5张图片
                processResultImages.push(original);// 处理成功就将上一张图片放进数组中
            else {// 丢弃数组中第一张
                URL.revokeObjectURL(processResultImages[0]);// 释放内存
                setProcessResultImages([...processResultImages.slice(1, 10), original]);
            }

            clearAllPaint();// 清除之前绘制的区域

            const blob = await fetch(res)
                .then(r => r.blob());
            setOriginalLoaded(URL.createObjectURL(blob));

            setConverted(true);
            setIsLoading(false);
        }).catch((error) => {
            console.log(error);
            showAlert({
                message: "Failed to process inpaint",
                type: "error"
            })
            setIsLoading(false);
        });
    }

    const backToCurImgPoint = (imgUrl: string, index: number) => {
        if (index + 1 < processResultImages.length)
            processResultImages
                .slice(index + 1)
                .map((clearImgUrl) => {
                    URL.revokeObjectURL(clearImgUrl);
                });

        setProcessResultImages(processResultImages.slice(0, index + 1));
        setOriginalLoaded(imgUrl);
    }

    const downloadImage = async () => {
        const response = await fetch(original);
        const blob = await response.blob();
        const fileUrl = URL.createObjectURL(blob);
        basicDownloadImage(fileUrl, `IMG_${Date.now()}.png`, false);
    };

    return (
        <div
            className="i-editor-container" onContextMenu={(e) => e.preventDefault()}>
            <div className="i-editor-contain">
                <div className="i-left-container">
                    <div className="i-editor">
                        <div
                            // @ts-ignore
                            ref={canvasContainerRef}
                            className={`canvas-container ${isGrabImg ? 'cursor-grab active:cursor-grabbing' : 'cursor-none'}`}
                            onWheel={zoomImage}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseLeave}
                        >
                            <CubeGridLoader
                                dotBackgroundColor="#3498db"
                                isOpen={isLoading}
                            />
                            {/*圆圈画笔或者橡皮擦*/}
                            {isShowPenOrEraser &&
                                <div
                                    style={{
                                    width: `${penSize.width / 2}px`,
                                    height: `${penSize.height / 2}px`,
                                    borderRadius: '50%',
                                    border: `3px solid ${penColor}`,
                                    backgroundColor: 'transparent',
                                    position: 'absolute',
                                    left: `${mousePosition.x}px`,
                                    top: `${mousePosition.y}px`,
                                    transform: `scale(${scale}) translate(-50%, -50%)`,
                                    cursor: 'none',
                                    pointerEvents: 'none',
                                    zIndex: '2025'
                                }}/>
                            }
                            {/*原始图像*/}
                            <img
                                // @ts-ignore
                                ref={originalCanvasRef}
                                src={`${original}`}
                                style={{
                                    backgroundColor: showImageStyle.backgroundColor,
                                    backgroundImage: showImageStyle.backgroundImage,
                                    backgroundPosition: showImageStyle.backgroundPosition,
                                    backgroundClip: showImageStyle.backgroundClip,
                                    backgroundSize: showImageStyle.backgroundSize,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    transform: `scale(${scale}) translate(${translateX}px, ${translateY}px)`,
                                    transition: dragState.current.isDragging ? 'none' : 'transform 0.1s ease-out',
                                    transformOrigin: transformOrigin
                                }}
                            />
                            {/* 涂鸦画布 */}
                            <canvas
                                className="rounded-sm"
                                // @ts-ignore
                                ref={canvasDrawContainerRef}
                                style={{
                                    backgroundColor: 'transparent',
                                    backgroundPosition: showImageStyle.backgroundPosition,
                                    backgroundClip: showImageStyle.backgroundClip,
                                    backgroundSize: showImageStyle.backgroundSize,
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    transform: `scale(${scale}) translate(${translateX}px, ${translateY}px)`,
                                    transition: dragState.current.isDragging ? 'none' : 'transform 0.1s ease-out',
                                    transformOrigin: transformOrigin
                                }}
                            />
                        </div>
                        <div className="i-bottom-opt-button">
                            <div></div>
                            <div className="flex gap-[5%]">
                                <Tooltip placement="bottom" title="Undo">
                                    <button onClick={undoDraw}>
                                        <Undo style={{width: '20px', height: '18px'}} />
                                    </button>
                                </Tooltip>
                                <Tooltip placement="bottom" title="Redo">
                                    <button onClick={redoDraw}>
                                        <Redo style={{width: '20px', height: '18px'}} />
                                    </button>
                                </Tooltip>
                                <Tooltip placement="bottom" title="Zoom in">
                                    <button onClick={zoomInImage}>
                                        <ZoomIn style={{width: '20px', height: '18px'}} />
                                    </button>
                                </Tooltip>
                                <Tooltip placement="bottom" title="Zoom out">
                                    <button onClick={zoomOutImage}>
                                        <ZoomOut style={{width: '20px', height: '18px'}} />
                                    </button>
                                </Tooltip>
                                <Tooltip placement="bottom" title='Grab'>
                                    <button onClick={setIsDragImg}>
                                        <Grab style={{width: '20px', height: '18px', color: grabColor}} />
                                    </button>
                                </Tooltip>
                                <Tooltip placement="bottom" title="Reupload">
                                    <button onClick={handleUpload}>
                                        <Upload style={{width: '20px', height: '18px'}}/>
                                        <input
                                            // @ts-ignore
                                            ref={fileInputRef}
                                            type="file"
                                            className="hidden"
                                            onChange={uploadFile}
                                            accept="image/png, image/jpeg, image/webp"
                                        />
                                    </button>
                                </Tooltip>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="i-right-container">
                    {processResultImages.length > 0 &&
                        <>
                            <h6 className={`text-[${penColor}] select-none flex`}>Historical IMG</h6>
                            <div
                                style={{scrollbarWidth: 'thin'}}
                                className={`flex flex-column h-[10vh] text-[black] gap-[6px] border border-[rgba(0, 0, 0, 0.2)] overflow-x-auto rounded-md select-none`}
                            >
                                {processResultImages.map((imgUrl, index) => (
                                    <img
                                        onClick={() => backToCurImgPoint(imgUrl, index)}
                                        src={imgUrl}
                                        className={`flex h-[100%] w-[30%] border border-[${penColor}] border-solid justify-center items-center cursor-pointer rounded-md`}
                                    />
                                ))}
                            </div>
                        </>
                    }
                    <h6 className={`text-[${penColor}] select-none flex`}>Brush Size</h6>
                    <Slider
                        min={sliderMin}
                        max={sliderMax}
                        defaultValue={30}
                        onChange={sliderSizeChange}
                        marks={{
                            [sliderMax]: sliderMax,
                        }}
                        styles={{
                            track: {
                                backgroundColor: penColor,
                            },
                        }}
                    />
                    <div className="i-landing-tool-business-button">
                        <Tooltip placement="bottom" title="inpaint image">
                            <div className="vip-opt-button i-opt-button" onClick={inpaint}>
                                Inpaint it
                            </div>
                        </Tooltip>
                        {isConverted &&
                            <>
                                <Tooltip placement="bottom" title="Up to 5000 x 5000 px">
                                    <div className="vip-opt-button i-opt-button">
                                        Download HD
                                    </div>
                                </Tooltip>

                                <Tooltip placement="bottom" title="Up to original size">
                                    <div className="ordinary-opt-button i-opt-button" onClick={downloadImage}>
                                        Download Free
                                    </div>
                                </Tooltip>
                            </>
                        }
                    </div>

                    <div className="absolute top-0 right-[6px]" onClick={closeEditorTable}>
                        <button className="i-close-btn">
                            <X style={{width: '20px', height: '18px'}} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}
